import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Header from "../../../components/Header";
import FooterV2 from "../../../components/FooterV2";
import SearchInputV2 from "../../../components/Ecommerce/SearchInputV2";
import LongHouseePet from "../../../components/Ecommerce/LongHouseePet";
import Policy from "../../../components/Ecommerce/Policy";
import { SHOPEEFOOD_POLICY } from "../../../components/Ecommerce/policy-content";
import AnswerQuestion from "../../../components/Q&A/Q&A";
import shopeefoodApi from "../../../api/shopeefoodApi";
import { LinkSource } from "../../../constants/link-source";
import ShopeeFoodLinkResult from "./shopeefood-link-result";
import CashBackGuide from "../../../components/Ecommerce/CashBackGuide";
import { useSharedLinkParam } from "../../../hooks/useSharedLinkParam";
import { useWrongPlatformRedirect } from "../../../hooks/useWrongPlatformRedirect";

/**
 * Đặt món hoàn tiền ShopeeFood.
 *
 * ShopeeFood không có API sản phẩm/hoa hồng như Shopee/Lazada nên trang này không
 * hiển thị thông tin món; chỉ dán link quán/món → backend gắn tracking → mở link đặt món.
 * Bố cục dùng chung với các sàn khác (SearchInputV2 → kết quả → hướng dẫn → chính sách → hỏi đáp).
 */

// Bài viết chứa bình luận hỏi đáp riêng của ShopeeFood (Posts.category='shopeefood')
const SHOPEEFOOD_POST_ID = 64;

const GRADIENT_FROM = "from-[#ff7a00]";
const GRADIENT_TO = "to-[#ffb020]";

function ShopeeFoodNew() {
    const { isLogined } = useSelector((state) => state.auth);
    const [searchTerm, setSearchTerm] = useState("");
    // Link dán sẵn qua ?link= — dùng khi user dán nhầm link ShopeeFood ở trang sàn khác
    // và được chuyển sang đây (useWrongPlatformRedirect).
    const { initialLink } = useSharedLinkParam(searchTerm);
    useEffect(() => { if (initialLink) setSearchTerm(initialLink); }, [initialLink]);
    const [resultLink, setResultLink] = useState("");
    // Bản rút gọn Sansale (copy/chia sẻ); rỗng khi Sansale lỗi → dùng resultLink
    const [resultShortLink, setResultShortLink] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const creatingRef = useRef(false);

    // Dán nhầm sàn → mở trang đúng thay vì báo "link chưa đúng" (link Shopee cũng bắt
    // đầu bằng http nên lọt qua kiểm tra URL bên dưới rồi chết ở API).
    const redirectIfWrongPlatform = useWrongPlatformRedirect("shopeefood");

    useEffect(() => {
        const link = searchTerm.trim();
        if (redirectIfWrongPlatform(link)) return;
        if (!link) {
            setResultLink("");
            setResultShortLink("");
            setError("");
            return;
        }
        if (!/^https?:\/\//i.test(link)) {
            setResultLink("");
            setResultShortLink("");
            setError("Link chưa đúng. Hãy dán link quán/món ShopeeFood (bắt đầu bằng http).");
            return;
        }
        if (creatingRef.current) return;

        let alive = true;
        const createLink = async () => {
            creatingRef.current = true;
            setError("");
            setResultLink("");
            setResultShortLink("");
            setIsLoading(true);
            try {
                const { data } = await shopeefoodApi.getLink(link, LinkSource.Converter);
                const affLink = data?.data?.link;
                if (!alive) return;
                if (affLink) {
                    setResultLink(affLink);
                    setResultShortLink(data?.data?.shortLink || "");
                } else setError("Không thể tạo link. Vui lòng thử lại.");
            } catch (err) {
                if (!alive) return;
                setError(
                    err?.response?.data?.message ||
                        "Không thể tạo link. Vui lòng kiểm tra lại link ShopeeFood."
                );
            } finally {
                creatingRef.current = false;
                if (alive) setIsLoading(false);
            }
        };
        createLink();

        return () => {
            alive = false;
        };
    }, [searchTerm, redirectIfWrongPlatform]);

    const hasResult = isLoading || !!resultLink || !!error;

    return (
        <div className="min-h-dvh flex flex-col justify-between">
            <Header />
            <div className="pt-16 w-full md:w-[768px] lg:w-[1024px] mx-auto flex flex-col gap-2">
                <div>
                    <SearchInputV2
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        isLoading={isLoading}
                        platform={"shopeefood"}
                        placeholder="Link quán/món ShopeeFood"
                        cashBackTitle={
                            <span className="text-xs">
                                🔗 Dán link → 🍜 Đặt qua app ShopeeFood → 💰 Hoàn tiền về ví
                            </span>
                        }
                        titleTextColor={"text-orange-600"}
                        borderInputColor={"border-orange-500"}
                    />
                </div>

                {!isLogined && (
                    <div className="px-2">
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                            Bạn chưa đăng nhập —{" "}
                            <Link to="/login" className="font-bold underline">
                                đăng nhập
                            </Link>{" "}
                            trước khi tạo link để đơn được ghi nhận hoàn tiền.
                        </p>
                    </div>
                )}

                {!hasResult && (
                    <LongHouseePet
                        platform={"ShopeeFood"}
                        message="Dán link quán/món ShopeeFood để Longhousee tạo link đặt món hoàn tiền cho nhen!"
                    />
                )}

                {hasResult && (
                    <ShopeeFoodLinkResult
                        link={resultLink}
                        shortLink={resultShortLink}
                        isLoading={isLoading}
                        errorMsg={error}
                    />
                )}

                {!resultLink && (
                    <div className="p-2 bg-surface">
                        <CashBackGuide
                            platform="shopeefood"
                            imagePlatform="shopee"
                            title="ĐẶT MÓN HOÀN TIỀN"
                            fromColorBg={GRADIENT_FROM}
                            toColorBg={GRADIENT_TO}
                            borderColor="border-orange-500"
                        />
                    </div>
                )}

                <Policy fromColorBg={GRADIENT_FROM} toColorBg={GRADIENT_TO} data={SHOPEEFOOD_POLICY} />
                <AnswerQuestion
                    postId={SHOPEEFOOD_POST_ID}
                    fromColorBg={GRADIENT_FROM}
                    toColorBg={GRADIENT_TO}
                />
            </div>
            <FooterV2 />
        </div>
    );
}

export default ShopeeFoodNew;
